import { useEffect, useState } from "react";
import { api, API } from "../../lib/api";
import { Store, ExternalLink } from "lucide-react";

/**
 * Our 2026 Marketplace — renders only vendors whose logo has passed
 * all three admin gates and been explicitly published. Returns null
 * when no vendors are approved yet so the homepage stays clean.
 */
export default function VendorMarketplace() {
  const [vendors, setVendors] = useState(null);

  useEffect(() => {
    let alive = true;
    api.get("/vendors/published")
      .then((r) => { if (alive) setVendors(r.data || []); })
      .catch(() => { if (alive) setVendors([]); });
    return () => { alive = false; };
  }, []);

  if (!vendors || vendors.length === 0) return null;

  // Marquee needs enough items to feel like a strip; dupe content if sparse.
  const row = vendors.length < 6 ? [...vendors, ...vendors, ...vendors] : [...vendors, ...vendors];

  return (
    <section
      id="marketplace"
      data-testid="vendor-marketplace-section"
      className="relative py-20 md:py-24 border-t border-white/10 overflow-hidden"
    >
      <div className="max-w-[1500px] mx-auto px-6 md:px-12">
        <div className="flex items-end justify-between gap-6 flex-wrap mb-10">
          <div>
            <div className="flex items-center gap-3 mb-4">
              <Store size={18} className="text-aawta-pink" />
              <p className="text-overline text-aawta-pink">OUR 2026 MARKETPLACE</p>
            </div>
            <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-light text-white tracking-tight leading-[1.08]">
              Vendors on the<br /> reception floor.
            </h2>
          </div>
          <p className="text-white/55 text-sm max-w-sm leading-relaxed">
            Approved brands joining us at the Power to Drive Change Leadership &amp; Impact
            Reception &mdash; curated across logistics, lifestyle, wellness, and professional
            services.
          </p>
        </div>
      </div>

      {/* Marquee strip */}
      <div
        data-testid="vendor-marketplace-marquee"
        className="relative"
        style={{ maskImage: "linear-gradient(to right, transparent, black 10%, black 90%, transparent)",
                 WebkitMaskImage: "linear-gradient(to right, transparent, black 10%, black 90%, transparent)" }}
      >
        <div className="flex gap-10 md:gap-14 animate-marquee items-center py-6 whitespace-nowrap">
          {row.map((v, i) => (
            <VendorTile key={`${v.business_name}-${i}`} v={v} />
          ))}
        </div>
      </div>

      <style>{`
        @keyframes aawta-marquee {
          from { transform: translateX(0); }
          to { transform: translateX(-50%); }
        }
        .animate-marquee {
          animation: aawta-marquee 42s linear infinite;
          width: max-content;
        }
        .animate-marquee:hover { animation-play-state: paused; }
      `}</style>
    </section>
  );
}

function VendorTile({ v }) {
  const src = v.logo_url?.startsWith("http")
    ? v.logo_url
    : `${API}${v.logo_url.replace(/^\/api/, "")}`;
  const inner = (
    <div
      data-testid={`vendor-tile-${v.business_name.toLowerCase().replace(/\s+/g, "-")}`}
      className="group inline-flex flex-col items-center gap-3 min-w-[160px] md:min-w-[200px]"
    >
      <div className="relative w-32 h-20 md:w-40 md:h-24 bg-white/5 border border-white/10 flex items-center justify-center overflow-hidden transition-all duration-500 group-hover:border-aawta-pink/40 group-hover:bg-white/10">
        <img
          src={src}
          alt={`${v.business_name} logo`}
          loading="lazy"
          className="max-w-[80%] max-h-[72%] object-contain filter grayscale opacity-70 group-hover:grayscale-0 group-hover:opacity-100 transition-all duration-500"
        />
      </div>
      <div className="text-center">
        <p className="font-display text-sm text-white tracking-wide whitespace-normal max-w-[200px]">
          {v.business_name}
        </p>
        {v.product_category && (
          <p className="text-[10px] tracking-[0.2em] uppercase text-white/40 mt-1">
            {v.product_category}
          </p>
        )}
      </div>
    </div>
  );

  if (v.website) {
    const href = v.website.startsWith("http") ? v.website : `https://${v.website}`;
    return (
      <a href={href} target="_blank" rel="noopener noreferrer" className="shrink-0" aria-label={`${v.business_name} website`}>
        {inner}
        <span className="sr-only">(opens in new tab)</span>
        <ExternalLink className="hidden" />
      </a>
    );
  }
  return <div className="shrink-0">{inner}</div>;
}
